import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled, { withTheme } from 'styled-components';
import { Device, DeviceProvider } from '@codex/gadgets';
import { debounce } from '@codex/helpers';
import {
  LAYOUT_1_0, LAYOUT_1_2, LAYOUT_2_0, LAYOUT_2_2
} from './constants';
import HomepageBox from './HomepageBox/index';
import RecentOrders from './RecentOrders/index';
import RecentlySaved from './RecentlySaved/index';
import { Provider as HomepageContextProvider } from './context';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  
  ${HomepageBox} {
    width: ${({ layout }) => ([LAYOUT_1_2, LAYOUT_2_2].includes(layout) ? '50%' : '100%')};
    min-width: 0;
  }
  
  ${HomepageBox} + ${HomepageBox} {
    margin-left: ${({ theme }) => theme.spacing[4]};
  }
  
  @media ${({ theme }) => theme.breakpoints.wrapGroup}{
    flex-direction: column;
    
    ${HomepageBox} {
      width: 100%;
    }
    
    ${HomepageBox} + ${HomepageBox} {
      margin-left: 0;
      margin-top: ${({ theme }) => theme.spacing[3]};
    }
  }
`;

class Homepage extends PureComponent {

  displayName = 'Homepage';

  static propTypes = {
    guest: PropTypes.bool,
    noRatingsLabel: PropTypes.string,
    orders: PropTypes.arrayOf(PropTypes.shape({})),
    ordersTitle: PropTypes.node,
    wishlist: PropTypes.arrayOf(PropTypes.shape({})),
    wishlistTitle: PropTypes.node,
    onRegisterAccountLinkClicked: PropTypes.func,
    registerAccountMessageStart: PropTypes.string,
    registerAccountMessageEnd: PropTypes.string,
    theme: PropTypes.shape({
      breakpoints: PropTypes.shape({
        forcedLayout: PropTypes.shape({})
      })
    }).isRequired
  };

  static defaultProps = {
    guest: false,
    noRatingsLabel: undefined,
    orders: [],
    ordersTitle: undefined,
    wishlist: [],
    wishlistTitle: undefined,
    onRegisterAccountLinkClicked: () => {},
    registerAccountMessageStart: undefined,
    registerAccountMessageEnd: undefined
  };

  state = {
    forcedOrdersLayout: undefined
  };

  componentDidMount() {
    if (typeof window !== 'undefined') {
      this.debouncedHandleResize = debounce(this.handleResize, 300).bind(this);
      this.handleResize();
      window.addEventListener('resize', this.debouncedHandleResize);
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.debouncedHandleResize);
    }
  }

  handleResize = () => {
    const { props } = this;

    if (typeof props.theme.breakpoints.forcedLayout === 'object') {
      for (const [layout, condition] of Object.entries(props.theme.breakpoints.forcedLayout)) {
        if (condition(props.orders)) {
          this.setState({
            forcedOrdersLayout: layout
          });
          break;
        }
        this.setState({
          forcedOrdersLayout: undefined
        });
      }
    }
  };

  render() {
    const { props, state } = this;

    let layout;

    if (props.orders.length >= 2 && props.wishlist.length >= 2) {
      layout = LAYOUT_2_2;
    } else if (props.orders.length === 1 && props.wishlist.length >= 2) {
      layout = LAYOUT_1_2;
    } else if (props.orders.length >= 2 && props.wishlist.length < 2) {
      layout = LAYOUT_2_0;
    } else if (props.orders.length === 1 && props.wishlist.length < 2) {
      layout = LAYOUT_1_0;
    }

    if (!layout) {
      return <noscript />;
    }

    const originalLayout = layout;

    return (
      <HomepageContextProvider value={{ guest: props.guest }}>
        <DeviceProvider>
          <Device>
            {({ deviceType, isMobile }) => {

              if (isMobile) {
                layout = LAYOUT_2_2;
              } else {
                layout = originalLayout;
              }

              return (
                <Wrapper layout={layout}>
                  <HomepageBox title={props.ordersTitle}>
                    <RecentOrders
                      deviceType={deviceType}
                      orders={props.orders}
                      layout={state.forcedOrdersLayout || layout}
                      onRegisterAccountLinkClicked={props.onRegisterAccountLinkClicked}
                      registerAccountMessageStart={props.registerAccountMessageStart}
                      registerAccountMessageEnd={props.registerAccountMessageEnd}
                    />
                  </HomepageBox>
                  {[LAYOUT_2_2, LAYOUT_1_2].includes(layout) && props.wishlist.length > 1 && (
                    <HomepageBox title={props.wishlistTitle}>
                      <RecentlySaved
                        deviceType={deviceType}
                        items={props.wishlist}
                        noRatingsLabel={props.noRatingsLabel}
                      />
                    </HomepageBox>
                  )}
                </Wrapper>
              );
            }}
          </Device>
        </DeviceProvider>
      </HomepageContextProvider>
    );
  }

}

export default withTheme(Homepage);
